import { useState } from 'react';
import { Button, ButtonGroup, Form, FormLayout, TextField } from '@customerio/pluma-components/react';
import { MPARTICLE_ANALYTICS_META } from '../analytics';
import { useAnalytics } from '@/services/analytics';

/** What the secret field shows once connected — the real value is never returned. */
const SECRET_PLACEHOLDER = '••••••••';

type ConnectFormProps = {
  /** The stored server key, or an empty string when not connected. */
  serverKey: string;
  isConnected: boolean;
  isReadOnly: boolean;
  /**
   * Whether the account may remove the connector. Separate from `isReadOnly`
   * because `journeys/mParticle/delete` is granted separately from
   * `journeys/mParticle/edit`, so a role can configure without disconnecting.
   */
  canRemove: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  /** True while a settings save is in flight; see {@link ConnectForm} on Remove. */
  isSaving: boolean;
  /**
   * Awaited, so Pluma's `Form` locks the whole fieldset for the duration. That
   * lock is invisible on the button, so `isConnecting` drives the spinner as
   * well — Ember disabled the button and swapped its label to 'Connecting…',
   * and the wait is real: the backend verifies the credentials against
   * mParticle before storing them.
   */
  onConnect: (credentials: { key: string; secret: string }) => Promise<void>;
  onDisconnect: () => void;
};

/**
 * The credentials half of the page: connect with a server key and secret, or
 * remove an existing connection.
 *
 * Both fields go read-only once connected, because the API takes credentials
 * only on create — changing them means removing the connection and connecting
 * again, which is what Ember did too.
 *
 * Remove is held while a settings save is in flight. Ember could afford to leave
 * it live: a save landing after a delete only pushed the record back into the
 * Ember Data store, while the controller had already dropped its reference, so
 * the page stayed on the connect state. Here the save writes the shared cache
 * slot the page reads from, so the same interleaving would show a deleted
 * connector as connected.
 */
export function ConnectForm({
  serverKey,
  isConnected,
  isReadOnly,
  canRemove,
  isConnecting,
  isDisconnecting,
  isSaving,
  onConnect,
  onDisconnect,
}: ConnectFormProps) {
  const { track } = useAnalytics();
  const [key, setKey] = useState(serverKey);
  const [secret, setSecret] = useState(isConnected ? SECRET_PLACEHOLDER : '');

  const isValid = key.trim() !== '' && secret.trim() !== '';

  const handleSubmit = async () => {
    if (!isValid) return;
    void track('integration', 'integrationEnableStarted', MPARTICLE_ANALYTICS_META);
    await onConnect({ key, secret });
  };

  const handleDisconnect = () => {
    void track('integration', 'integrationDisableStarted', MPARTICLE_ANALYTICS_META);
    onDisconnect();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        <TextField
          label="Server key"
          value={key}
          onInput={(event) => setKey(event.target.value)}
          isDisabled={isConnected || isReadOnly}
        />
        <TextField
          label="Server secret"
          value={secret}
          onInput={(event) => setSecret(event.target.value)}
          isDisabled={isConnected || isReadOnly}
        />

        <ButtonGroup groupVariant="spaced">
          {isConnected ? (
            <Button
              variant="secondary"
              isCritical
              isDisabled={!canRemove || isSaving}
              isLoading={isDisconnecting}
              onClick={handleDisconnect}
            >
              Remove integration
            </Button>
          ) : (
            <Button type="submit" variant="primary" isDisabled={isReadOnly || !isValid} isLoading={isConnecting}>
              Connect mParticle
            </Button>
          )}
        </ButtonGroup>
      </FormLayout>
    </Form>
  );
}
